import React, { useState, useEffect, useRef } from 'react';
import moment from 'moment';
import { injectIntl } from 'react-intl';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

const CalendarSvg = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="18"
    height="17"
    viewBox="0 0 18 17"
    style={{ position: 'absolute', right: 10, top: 11 }}>
    <g fill="none">
      <path
        stroke="currentColor"
        strokeWidth="1.2"
        d="M1 4.5h16v11a1 1 0 0 1-1 1H2a1 1 0 0 1-1-1v-11zm0 0V3a1 1 0 0 1 1-1h14a1 1 0 0 1 1 1v1.5"
      />
      <path stroke="currentColor" strokeLinecap="round" strokeWidth="1.2" d="M5.5 1v3M12.5 1v3M1 8h16" />
    </g>
  </svg>
);

const buildDays = (year, month) => {
  const first = new Date(year, month, 1).getDay();
  const total = new Date(year, month + 1, 0).getDate();
  const days = [];
  for (let i = 0; i < first; i += 1) days.push(null);
  for (let d = 1; d <= total; d += 1) days.push(d);
  return days;
};

const pad = n => String(n).padStart(2, '0');
const toStr = (y, m, d) => `${y}-${pad(m + 1)}-${pad(d)}`;

const MiniCalendar = ({ label, selected, minDate, maxDate, onChange }) => {
  const today = new Date();
  const initDate = selected ? new Date(selected) : today;
  const [year, setYear] = useState(initDate.getFullYear());
  const [month, setMonth] = useState(initDate.getMonth());
  const [mode, setMode] = useState('day');

  const days = buildDays(year, month);
  const todayStr = toStr(today.getFullYear(), today.getMonth(), today.getDate());

  const prevMonth = () => {
    if (month === 0) {
      setYear(y => y - 1);
      setMonth(11);
    } else {
      setMonth(m => m - 1);
    }
  };

  const nextMonth = () => {
    if (month === 11) {
      setYear(y => y + 1);
      setMonth(0);
    } else {
      setMonth(m => m + 1);
    }
  };

  const selectDay = d => {
    if (!d) return;
    const s = toStr(year, month, d);
    if (maxDate && s > maxDate) return;
    if (minDate && s < minDate) return;
    onChange(s);
  };

  const selectMonth = m => {
    setMonth(m);
    setMode('day');
  };

  const selectYear = y => {
    setYear(y);
    setMode('month');
  };

  const yearStart = Math.floor(year / 12) * 12;

  const hdr = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 8,
  };
  const arrowBtn = {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    fontSize: 16,
    color: '#555',
    padding: '2px 6px',
    lineHeight: 1,
  };
  const titleBtn = {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    fontWeight: 600,
    fontSize: 13,
    color: '#333',
    padding: '2px 4px',
  };

  return (
    <div style={{ width: 220 }}>
      <div
        style={{
          fontSize: 11,
          fontWeight: 600,
          color: '#888',
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
          marginBottom: 6,
        }}>
        {label}
      </div>

      {mode === 'day' && (
        <>
          <div style={hdr}>
            <button type="button" style={arrowBtn} onClick={prevMonth}>
              &#8249;
            </button>
            <span>
              <button type="button" style={titleBtn} onClick={() => setMode('month')}>
                {MONTHS[month]}
              </button>
              <button type="button" style={titleBtn} onClick={() => setMode('year')}>
                {year}
              </button>
            </span>
            <button type="button" style={arrowBtn} onClick={nextMonth}>
              &#8250;
            </button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: 2 }}>
            {WEEKDAYS.map(w => (
              <div
                key={w}
                style={{ textAlign: 'center', fontSize: 11, color: '#aaa', fontWeight: 600, paddingBottom: 4 }}>
                {w}
              </div>
            ))}
            {days.map((d, i) => {
              const ds = d ? toStr(year, month, d) : null;
              const isSelected = ds === selected;
              const isToday = ds === todayStr;
              const disabled = ds && ((maxDate && ds > maxDate) || (minDate && ds < minDate));
              return (
                <button
                  key={i} // eslint-disable-line react/no-array-index-key
                  type="button"
                  onClick={() => selectDay(d)}
                  disabled={!d || disabled}
                  style={{
                    textAlign: 'center',
                    fontSize: 13,
                    padding: '5px 0',
                    borderRadius: 4,
                    border: 'none',
                    cursor: d && !disabled ? 'pointer' : 'default',
                    background: isSelected ? '#2fa7dc' : 'none',
                    color: isSelected ? '#fff' : disabled ? '#ccc' : isToday ? '#2fa7dc' : '#333',
                    fontWeight: isToday && !isSelected ? 700 : 400,
                  }}>
                  {d || ''}
                </button>
              );
            })}
          </div>
        </>
      )}

      {mode === 'month' && (
        <>
          <div style={hdr}>
            <button type="button" style={arrowBtn} onClick={() => setYear(y => y - 1)}>
              &#8249;
            </button>
            <button type="button" style={titleBtn} onClick={() => setMode('year')}>
              {year}
            </button>
            <button type="button" style={arrowBtn} onClick={() => setYear(y => y + 1)}>
              &#8250;
            </button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 4 }}>
            {MONTHS.map((mn, idx) => (
              <button
                key={mn}
                type="button"
                onClick={() => selectMonth(idx)}
                style={{
                  padding: '7px 0',
                  borderRadius: 4,
                  border: 'none',
                  cursor: 'pointer',
                  background: idx === month ? '#2fa7dc' : 'none',
                  color: idx === month ? '#fff' : '#333',
                  fontSize: 13,
                }}>
                {mn}
              </button>
            ))}
          </div>
        </>
      )}

      {mode === 'year' && (
        <>
          <div style={hdr}>
            <button type="button" style={arrowBtn} onClick={() => setYear(yearStart - 12)}>
              &#8249;
            </button>
            <span style={{ fontSize: 13, fontWeight: 600, color: '#333' }}>
              {yearStart} - {yearStart + 11}
            </span>
            <button type="button" style={arrowBtn} onClick={() => setYear(yearStart + 12)}>
              &#8250;
            </button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 4 }}>
            {Array.from({ length: 12 }, (_, i) => yearStart + i).map(y => (
              <button
                key={y}
                type="button"
                onClick={() => selectYear(y)}
                style={{
                  padding: '7px 0',
                  borderRadius: 4,
                  border: 'none',
                  cursor: 'pointer',
                  background: y === year ? '#2fa7dc' : 'none',
                  color: y === year ? '#fff' : '#333',
                  fontSize: 13,
                }}>
                {y}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

const TradingDateRangeField = ({ value, onChange, intl }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [startVal, setStartVal] = useState('');
  const [endVal, setEndVal] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    const parts = value ? value.split('_') : [];
    setStartVal(parts[0] || '');
    setEndVal(parts[1] || '');
  }, [value]);

  const displayText = startVal && endVal ? `${startVal} - ${endVal}` : '';

  useEffect(() => {
    const handleClick = e => {
      if (ref.current && !ref.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleApply = () => {
    if (startVal && endVal) {
      onChange(`${startVal}_${endVal}`);
      setIsOpen(false);
    }
  };

  const today = moment().format('YYYY-MM-DD');

  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-block', minWidth: 230 }}>
      <button
        type="button"
        className="DateRangeField__picker haveText"
        onClick={() => setIsOpen(o => !o)}
        style={{ width: '100%' }}>
        <span className="DateRangeField__picker-text">{displayText}</span>
        <CalendarSvg />
      </button>
      {isOpen && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            left: 0,
            zIndex: 9999,
            background: '#fff',
            border: '1px solid #e8e8e8',
            borderRadius: 8,
            padding: '16px 20px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
            display: 'flex',
            flexDirection: 'column',
            gap: 16,
            minWidth: 260,
          }}>
          <MiniCalendar
            label={intl.formatMessage({ id: 'justFrom' })}
            selected={startVal}
            maxDate={endVal || today}
            onChange={setStartVal}
          />
          <div style={{ borderTop: '1px solid #f0f0f0' }} />
          <MiniCalendar
            label={intl.formatMessage({ id: 'justTo' })}
            selected={endVal}
            minDate={startVal}
            maxDate={today}
            onChange={setEndVal}
          />
          <button
            type="button"
            onClick={handleApply}
            style={{
              padding: '9px 0',
              borderRadius: 6,
              background: '#2fa7dc',
              color: '#fff',
              border: 'none',
              cursor: 'pointer',
              fontSize: 14,
              fontWeight: 500,
            }}>
            {intl.formatMessage({ id: 'justApply' })}
          </button>
        </div>
      )}
    </div>
  );
};

export default injectIntl(TradingDateRangeField);
